import {
  insurance as baseInsurance,
  type CaseDraftAttachmentModel,
  type PostByContractIdAlterationApiArg,
  type PostCasedraftsByCaseDraftIdAttachmentsApiArg,
} from './baseApi/insurance'
import type { OverrideResults } from './overrideResult'

export type * from './baseApi/insurance'
export {
  AdditionalPropertyDamage,
  CaseFollowUp,
  ConsequentialDamage,
  ContractStatus as InsuranceAgreementFilterStatus,
  EmailCommunication,
  IsResponsibleForDamage,
  OtherContactPerson,
  PlaceOfDamage,
  SortColumn as InsuranceAgreementSortColumn,
  SortColumn2 as InsuranceCaseSortColumn,
  SortOrder,
  Status as InsuranceAgreementStatus,
  Status2 as InsuranceCaseStatus,
  Status3 as InsuranceClaimStatus,
  TransferStatus as InsuranceClaimTransferStatus,
  CreatedInApplication,
  ViewInApplication,
} from './baseApi/insurance'

export type PostByContractIdAlterationFormArg =
  PostByContractIdAlterationApiArg & {
    alteration: FormData
  }

export type PostCasedraftAttachmentsFormArg =
  PostCasedraftsByCaseDraftIdAttachmentsApiArg & {
    files: FormData
  }

export type InsuranceObjectModel = {
  id: string
  description: string
  denomination: string
}

export const insuranceApi = baseInsurance
  .enhanceEndpoints<
    'InsuranceClaims' | 'InsuranceClaim',
    OverrideResults<
      typeof baseInsurance,
      { getInsuranceObjects: InsuranceObjectModel[] }
    >
  >({
    addTagTypes: ['InsuranceClaims', 'InsuranceClaim'],
    endpoints: {
      postCasedraftsList: { providesTags: ['InsuranceClaims'] },
      getCasedraftsByCaseDraftId: { providesTags: ['InsuranceClaim'] },
      postCasedrafts: { invalidatesTags: ['InsuranceClaims'] },
      putCasedraftsByCaseDraftId: { invalidatesTags: ['InsuranceClaims'] },
      deleteCasedraftsByCaseDraftId: { invalidatesTags: ['InsuranceClaims'] },
      postCasedraftstatusByCaseDraftIdSentToBblAdvisor: {
        invalidatesTags: ['InsuranceClaims'],
      },
      postCasedraftstatusByCaseDraftIdAcceptedByBblAdvisor: {
        invalidatesTags: ['InsuranceClaims'],
      },
      postCasedraftstatusByCaseDraftIdRejectedByBblAdvisor: {
        invalidatesTags: ['InsuranceClaims'],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      postByContractIdAlteration: build.mutation<
        void,
        PostByContractIdAlterationFormArg
      >({
        query: ({ contractId, alteration }) => ({
          url: `/insurance/${contractId}/alteration`,
          method: 'POST',
          body: alteration,
        }),
      }),
      postCasedraftsByCaseDraftIdAttachments: build.mutation<
        CaseDraftAttachmentModel[],
        PostCasedraftAttachmentsFormArg
      >({
        query: ({ caseDraftId, files }) => ({
          url: `/insurance/casedrafts/${caseDraftId}/attachments`,
          method: 'POST',
          body: files,
        }),
      }),
    }),
    overrideExisting: true,
  })

export const {
  useGetClientByClientNoQuery,
  usePostClientByClientNoQuery,
  useGetClientByClientIdCasesAndCaseIdQuery,
  useGetClientByClientIdCasesAndCaseIdDocumentsBlobIdQuery,
  usePostClientByClientIdCasesQuery,
  useGetByContractIdIsconnectedtoifQuery,
  useGetByContractIdQuery,
  usePostByContractIdAlterationMutation,
  usePostCasedraftsMutation,
  useDeleteCasedraftsByCaseDraftIdMutation,
  useGetCasedraftsByCaseDraftIdQuery,
  usePutCasedraftsByCaseDraftIdMutation,
  useDeleteCasedraftsByCaseDraftIdAttachmentsAndCaseDraftAttachmentIdMutation,
  useGetCasedraftsByCaseDraftIdAttachmentsAndCaseDraftAttachmentIdQuery,
  useGetCasedraftsCountByContractIdAndStatusQuery,
  usePostCasedraftsListQuery,
  usePostCasedraftsByCaseDraftIdAttachmentsMutation,
  usePostCasedraftstatusByCaseDraftIdSentToBblAdvisorMutation,
  usePostCasedraftstatusByCaseDraftIdAcceptedByBblAdvisorMutation,
  usePostCasedraftstatusByCaseDraftIdRejectedByBblAdvisorMutation,
  useGetCaseTypesQuery,
  useGetInsuranceObjectsQuery,
  useGetUnitByUnitIdQuery,
  useGetUnitByUnitIdCasesAndCaseIdQuery,
  useGetUnitByUnitIdCasesAndCaseIdDocumentsBlobIdQuery,
  usePostUnitByUnitIdCasesMutation,
} = insuranceApi
